import { useCallback, useMemo, useRef, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { ArchiveIcon, ArchiveRestore, CloudCog, Info, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate, useParams } from 'react-router-dom';

import { ArchivedEntityAlert } from '@/components/archived-entity-alert';
import { StatusBadgeType, usePermissions } from '@/modules/common';
import {
  ActionButtonConfig,
  FeatureDetailsTemplate,
  FeatureMode,
  FeatureTab,
  useTabValidation,
} from '@/modules/common/components/templates';
import { IntegrationStatus } from '@/modules/common/types/integration-status';
import { IntegrationServices } from '@/modules/common/types/integrations';
import { OpsiumPolicy } from '@/modules/common/types/opsium-policy';
import { useIntegrationStatus } from '@/modules/onboarding/integrations-setup/hooks/use-integration-status';
import { AuthenticatedRoutes } from '@/router/routes';

import {
  ArchiveClientModal,
  DeleteClientsModal,
  UnarchiveClientModal,
  UpdateQuickBooksClientModal,
} from '../../components/modals';
import { useClientDetails, useClientDraft } from '../../hooks';
import {
  mapClientFormDataToCreateRequest,
  mapClientFormDataToUpdateRequest,
} from '../../mappers/client-request.mapper';
import { Client, ClientFormData, ClientStatus, ClientTab } from '../../types';
import { QuickBooksSyncedField } from '../../utils/quickbooks-synced-fields';
import {
  ClientInfoTab,
  ClientInfoTabRef,
  ClientIntegrationsTab,
  ClientIntegrationsTabRef,
} from './tab-pages';

export function ClientDetailsPage() {
  const { t } = useTranslation(['authenticated.clients', 'common']);
  const navigate = useNavigate();
  const { clientId } = useParams<{ clientId: string }>();
  const queryClient = useQueryClient();

  // Tab refs for form management
  const clientInfoFormRef = useRef<ClientInfoTabRef>(null);
  const clientIntegrationsFormRef = useRef<ClientIntegrationsTabRef>(null);

  // Modal state
  const [isArchiveModalOpen, setIsArchiveModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isQuickBooksModalOpen, setIsQuickBooksModalOpen] = useState(false);
  // Kept after closing, so the list does not empty while the dialog animates out
  const [quickBooksChangedFields, setQuickBooksChangedFields] = useState<QuickBooksSyncedField[]>(
    []
  );

  const handleQuickBooksConfirmationRequired = useCallback(
    (changedFields: QuickBooksSyncedField[]) => {
      setQuickBooksChangedFields(changedFields);
      setIsQuickBooksModalOpen(true);
    },
    []
  );

  // Draft state
  const [formStateChangeHandler, setFormStateChangeHandler] = useState<
    ((isDirty: boolean) => void) | null
  >(null);

  const {
    clientData,
    mode,
    currentTab,
    isLoading,
    isLoadingClient,
    isUploadingLogo,
    error,
    handleSaveClient,
    handleTabChange,
    handleLogoUpload,
  } = useClientDetails();

  const { data: integrationStatus } = useIntegrationStatus({ enablePolling: false });
  const quickBooksStatus = integrationStatus?.integrations.find(
    integration => integration.integration === IntegrationServices.QUICKBOOKS
  )?.status;
  // A connected QuickBooks also reports SYNCING while it syncs; only ERROR keeps the backend from writing to it
  const isQuickBooksConnected =
    quickBooksStatus !== undefined && quickBooksStatus !== IntegrationStatus.Error;
  const [hasPickedQuickBooksCustomer, setHasPickedQuickBooksCustomer] = useState(false);
  // Picking an existing customer on the Integrations tab links it instead of creating one
  const isCreatedInQuickBooks =
    mode === FeatureMode.Create && isQuickBooksConnected && !hasPickedQuickBooksCustomer;

  // Handler to refetch client data
  const handleRefetch = () => {
    queryClient.invalidateQueries({ queryKey: ['clients', clientId] });
  };

  // Check if all integrations are connected
  const hasIntegrationsTabWarning = Boolean(
    mode === FeatureMode.Edit &&
      clientData?.integrations?.some(integration => !integration.selectedValue?.id)
  );

  // Tab validation state management
  const { tabValid, handleSetTabValid } = useTabValidation([
    ClientTab.Info,
    ClientTab.Integrations,
  ]);

  // Draft management
  const { saveDraftData, getDraftData, clearDraft, hasDraftData, getFinalSubmissionData } =
    useClientDraft();

  // Get current tab draft data
  const currentTabDraftData = getDraftData(currentTab);

  // Get form state change handler from template
  const handleFormStateChangeHandlerReady = useCallback((handler: (isDirty: boolean) => void) => {
    setFormStateChangeHandler(() => handler);
  }, []);

  // Adapter function to convert ClientFormData to API request format
  const handleSave = useCallback(
    ({ data, successCallback }: { data: ClientFormData; successCallback?: () => void }) => {
      const getLoadedClient = (): Client => {
        if (!clientData) throw new Error('Cannot save a client that is not loaded');
        return clientData;
      };

      // Convert ClientFormData to the appropriate request type based on mode
      const requestData =
        mode === FeatureMode.Create
          ? mapClientFormDataToCreateRequest(data)
          : mapClientFormDataToUpdateRequest(data, getLoadedClient());

      // Get integrations data from integrations tab if it's the current tab
      const integrationsData = clientIntegrationsFormRef.current?.getFormData()?.integrations;

      handleSaveClient({ data: requestData, integrations: integrationsData, successCallback });
    },
    [handleSaveClient, mode, clientData, clientIntegrationsFormRef.current]
  );

  const handleQuickBooksUpdateConfirmed = useCallback(() => {
    const formData = clientInfoFormRef.current?.getFormData();
    if (formData) {
      handleSave({ data: formData });
    }
  }, [handleSave]);

  // Helper function to wrap tab content with archived alert when needed
  const wrapWithArchivedAlert = useCallback(
    (content: React.ReactNode) => {
      if (clientData?.status === ClientStatus.Archived) {
        return (
          <div className="space-y-4">
            <ArchivedEntityAlert
              message={t('authenticated.clients:details.archivedClientMessage')}
            />
            {content}
          </div>
        );
      }
      return content;
    },
    [clientData?.status, t]
  );

  const tabs = useMemo(
    () => [
      {
        id: ClientTab.Info,
        label: t('authenticated.clients:details.tabs.info.title'),
        icon: Info,
        hasError: !tabValid.info,
        ref: clientInfoFormRef,
        content: wrapWithArchivedAlert(
          <ClientInfoTab
            ref={clientInfoFormRef}
            mode={mode}
            featureData={clientData}
            draftData={mode === FeatureMode.Create ? currentTabDraftData : undefined}
            onFormStateChange={formStateChangeHandler || ((_isDirty: boolean) => {})}
            onQuickBooksConfirmationRequired={handleQuickBooksConfirmationRequired}
            isCreatedInQuickBooks={isCreatedInQuickBooks}
          />
        ),
      },
      {
        id: ClientTab.Integrations,
        label: t('authenticated.clients:details.tabs.integrations.title'),
        icon: CloudCog,
        hasError: !tabValid.integrations,
        ref: clientIntegrationsFormRef,
        hasWarning: hasIntegrationsTabWarning,
        content: wrapWithArchivedAlert(
          <ClientIntegrationsTab
            ref={clientIntegrationsFormRef}
            mode={mode}
            featureData={clientData}
            draftData={currentTabDraftData}
            rawIntegrations={clientData?.rawIntegrations}
            onFormStateChange={formStateChangeHandler || ((_isDirty: boolean) => {})}
            onQuickBooksCustomerPickedChange={setHasPickedQuickBooksCustomer}
          />
        ),
      },
    ],
    [
      mode,
      t,
      clientData,
      currentTabDraftData,
      formStateChangeHandler,
      handleQuickBooksConfirmationRequired,
      isCreatedInQuickBooks,
      hasIntegrationsTabWarning,
      clientIntegrationsFormRef,
      wrapWithArchivedAlert,
      hasIntegrationsTabWarning,
      tabValid,
    ]
  );

  // Custom validation logic for hasAnyData in create mode
  const calculateHasAnyData = useCallback(
    (context: {
      mode: FeatureMode;
      currentTab: string;
      tabs: FeatureTab<ClientFormData>[];
      getDraftData: (tab: string) => Partial<ClientFormData> | undefined;
      hasDraftData: boolean;
    }) => {
      if (context.mode === FeatureMode.Create) {
        // For create mode, only client name is required
        const infoDraft = context.getDraftData(ClientTab.Info);

        // Get current tab data if tab has changes but not yet saved to draft
        const infoTab = context.tabs.find(tab => tab.id === ClientTab.Info);

        const currentInfoData =
          context.currentTab === ClientTab.Info ? infoTab?.ref.current?.getFormData() : null;

        // Merge draft data with current unsaved data
        const finalInfoData = currentInfoData || infoDraft;

        // Only validate required field: client name
        const hasValidName = Boolean(finalInfoData?.name && finalInfoData.name.trim().length > 0);

        return hasValidName;
      }

      // For edit/read mode, let template handle default logic
      return false;
    },
    []
  );

  // Form state change handler
  const [_hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const handleFormStateChange = useCallback((isDirty: boolean) => {
    setHasUnsavedChanges(isDirty);
  }, []);

  const getStatusText = (status: ClientStatus) => {
    return status === ClientStatus.Active ? 'Active' : 'Archived';
  };

  const { hasPermission } = usePermissions();
  const canViewClients = hasPermission(OpsiumPolicy.ClientsViewAll);
  const isDeleteClientsEnabled = hasPermission(OpsiumPolicy.ClientsDelete);
  const isEditClientsEnabled = hasPermission(OpsiumPolicy.ClientsEdit);

  // Top right buttons to make actions related to client (archive/delete)
  const actionButtons: ActionButtonConfig[] = useMemo(() => {
    if (!clientData) return [];
    const buttons: ActionButtonConfig[] = [];

    if (isEditClientsEnabled) {
      buttons.push({
        label:
          clientData.status === ClientStatus.Archived
            ? t('authenticated.clients:archive.unarchiveClient')
            : t('authenticated.clients:archive.archiveClient'),
        icon: clientData.status === ClientStatus.Archived ? ArchiveRestore : ArchiveIcon,
        onClick: () => setIsArchiveModalOpen(true),
        variant: 'outline' as const,
      });
    }

    if (isDeleteClientsEnabled) {
      buttons.push({
        label: t('authenticated.clients:delete.deleteClientButton'),
        icon: Trash2,
        onClick: () => setIsDeleteModalOpen(true),
        variant: 'outlineDestructive' as const,
      });
    }

    return buttons;
  }, [clientData, t, isEditClientsEnabled, isDeleteClientsEnabled]);

  const footerProps = useMemo(
    () => ({
      labels: {
        saving: t('authenticated.clients:details.tabs.info.saving'),
        create: t('authenticated.clients:details.tabs.info.createClient'),
        saveChanges: t('authenticated.clients:details.tabs.info.saveChanges'),
      },
      showDatePicker: false, // No date picker for clients as per requirements
    }),
    [t]
  );

  const clientsForDelete = useMemo(() => {
    if (!clientData) return [];
    return [clientData];
  }, [clientData]);

  const modals = useMemo(
    () => (
      <>
        {/* Archive/Unarchive Modal - Externally controlled */}
        {isEditClientsEnabled &&
          clientData &&
          (clientData.status === ClientStatus.Archived ? (
            <UnarchiveClientModal
              client={clientData}
              open={isArchiveModalOpen}
              onOpen={() => setIsArchiveModalOpen(true)}
              onClose={() => setIsArchiveModalOpen(false)}
            />
          ) : (
            <ArchiveClientModal
              client={clientData}
              open={isArchiveModalOpen}
              onOpen={() => setIsArchiveModalOpen(true)}
              onClose={() => setIsArchiveModalOpen(false)}
            />
          ))}

        <UpdateQuickBooksClientModal
          open={isQuickBooksModalOpen}
          changedFields={quickBooksChangedFields}
          onConfirm={handleQuickBooksUpdateConfirmed}
          onClose={() => setIsQuickBooksModalOpen(false)}
        />

        {/* Delete Modal - Externally controlled */}
        {isDeleteClientsEnabled && clientData && (
          <DeleteClientsModal
            clientsForDelete={clientsForDelete}
            onSuccess={() => navigate(AuthenticatedRoutes.Clients)}
            open={isDeleteModalOpen}
            onOpen={() => setIsDeleteModalOpen(true)}
            onClose={() => setIsDeleteModalOpen(false)}
          />
        )}
      </>
    ),
    [
      clientData,
      isArchiveModalOpen,
      isDeleteModalOpen,
      clientsForDelete,
      navigate,
      isEditClientsEnabled,
      isDeleteClientsEnabled,
      isQuickBooksModalOpen,
      quickBooksChangedFields,
      handleQuickBooksUpdateConfirmed,
    ]
  );

  // Guard access — redirect to forbidden when View clients is disabled
  if (clientId !== 'new' && !canViewClients) {
    return <Navigate to={AuthenticatedRoutes.Forbidden} replace />;
  }

  return (
    <FeatureDetailsTemplate<Client, ClientFormData>
      entityData={clientData}
      mode={mode}
      statusBadgeType={StatusBadgeType.Client}
      currentTab={currentTab}
      isLoading={isLoading}
      isLoadingEntity={isLoadingClient}
      error={error}
      tabs={tabs}
      getDraftData={(tab: string) => getDraftData(tab as ClientTab)}
      saveDraftData={({ tab, data }) => saveDraftData({ tab: tab as ClientTab, data })}
      clearDraft={clearDraft}
      getFinalSubmissionData={getFinalSubmissionData}
      hasDraftData={hasDraftData}
      setTabValid={handleSetTabValid}
      calculateHasAnyData={context => {
        const result = calculateHasAnyData({
          mode: context.mode,
          currentTab: context.currentTab,
          tabs: context.tabs,
          getDraftData: context.getDraftData,
          hasDraftData: context.hasDraftData,
        });
        return result ?? true;
      }}
      handleSave={handleSave}
      // Actions
      onBack={() => navigate(AuthenticatedRoutes.Clients)}
      onFormStateChange={handleFormStateChange}
      onTabChange={handleTabChange}
      // Unsaved changes confirmation
      enableUnsavedChangesConfirmation={mode === FeatureMode.Edit || mode === FeatureMode.Create}
      unsavedChangesConfig={{
        title: t(
          mode === FeatureMode.Create
            ? 'authenticated.clients:details.confirmations.discardCreation.title'
            : 'authenticated.clients:details.confirmations.discardChanges.title'
        ),
        message: t(
          mode === FeatureMode.Create
            ? 'authenticated.clients:details.confirmations.discardCreation.message'
            : 'authenticated.clients:details.confirmations.discardChanges.message'
        ),
        confirmText: t(
          mode === FeatureMode.Create
            ? 'authenticated.clients:details.confirmations.discardCreation.confirm'
            : 'authenticated.clients:details.confirmations.discardChanges.confirm'
        ),
        confirmVariant: 'destructive',
      }}
      onFormStateChangeHandlerReady={handleFormStateChangeHandlerReady}
      onRefetch={handleRefetch}
      // Customization
      pageTitle={t('authenticated.clients:details.clientDetails')}
      createTitle={t('authenticated.clients:details.createClient')}
      loadingMessage={t('authenticated.clients:details.loadingClient')}
      errorMessage={t('common:error.reloadPage')}
      showAvatar={true}
      showStatus={true}
      avatarEditable={true}
      onAvatarChange={handleLogoUpload}
      isUploadingAvatar={isUploadingLogo}
      getStatusText={getStatusText}
      actionButtons={actionButtons}
      useGenericFooter={true}
      footerProps={footerProps}
      hideFooter={mode === FeatureMode.Read || clientData?.status === ClientStatus.Archived}
      modals={modals}
      errorActions={[
        {
          label: t('common:backToClients'),
          onClick: () => navigate(AuthenticatedRoutes.Clients),
          variant: 'secondary',
        },
      ]}
      patchResultToDisplay={null}
    />
  );
}
